// https://elazizi.com/posts/react-query-auth-token-refresh/#simple-project-setup
import type { AxiosError } from 'axios';
import { useMutation, useQuery } from '@tanstack/react-query';

import { api } from '../client';

import {
  CommonStatusRes,
  GetReviewUidReq,
  ReviewAnswerReq,
  ReviewReq,
  ReviewRequestedRes,
  ReviewRes,
} from '../types/';
import { enqueueSnackbar } from 'notistack';
import { UserContext } from '../context/userContext';
import React from 'react';
import { queryClient } from '..';

const getReview = ({ uid }: GetReviewUidReq) => {
  return api.get<AxiosError, ReviewRes>(`/v1/review/${uid}`);
};

const getReviewRequested = () => {
  return api.get<AxiosError, ReviewRequestedRes>('/v1/review/requested');
};

const getReviewAssigned = () => {
  return api.get<AxiosError, ReviewRequestedRes>('/v1/review/assigned');
};

const postReview = (reqData: ReviewReq) => {
  return api.post<AxiosError, ReviewRes>(`/v1/review`, reqData);
};

const postReviewAnswer = ({ uid, reviewer_note }: ReviewAnswerReq) => {
  return api.post<AxiosError, CommonStatusRes>(`/v1/review/${uid}/answer`, {
    reviewer_note,
  });
};

export const reviewService = (uid = '') => {
  const { loggedIn } = React.useContext(UserContext);

  const review = useQuery({
    queryKey: ['GET_REVIEW', uid],
    queryFn: () => getReview({ uid }),
    enabled: !!uid,
    // queryFn: ({ queryKey }) => getReview(queryKey[1]),
  });

  const requestedReviews = useQuery({
    queryKey: ['GET_REQUESTED_REVIEWS_LIST'],
    queryFn: getReviewRequested,
    enabled: loggedIn,
    staleTime: Infinity,
  });

  const assignedReviews = useQuery({
    queryKey: ['GET_ASSIGNED_REVIEWS_LIST'],
    queryFn: getReviewAssigned,
    enabled: loggedIn,
    staleTime: Infinity,
  });

  const reviewMutation = useMutation({
    mutationFn: postReview,
    onError: ({ message }) => {
      enqueueSnackbar(`requestedReviews error: ${message}`, {
        variant: 'error',
      });
    },
    onSuccess: ({
      data: {
        qa: { conversation_uid },
      },
    }) => {
      // Update related lists, to remove all related items linked to the deleted conversation
      queryClient.refetchQueries({ queryKey: ['GET_ASSIGNED_REVIEWS_LIST'] });
      queryClient.refetchQueries({ queryKey: ['GET_REQUESTED_REVIEWS_LIST'] });
      queryClient.refetchQueries({
        queryKey: ['GET_CONVERSATION', conversation_uid],
      });

      enqueueSnackbar('Successfully submitted SME comment.', {
        variant: 'success',
      });
    },
  });

  const reviewAnswerMutation = useMutation({
    mutationFn: postReviewAnswer,
    onError: ({ message }) => {
      enqueueSnackbar(`postReviewAnswer error: ${message}`, {
        variant: 'error',
      });
    },
    onSuccess: () => {},
  });

  return {
    review: review.data?.data,
    reviewRefetch: review.refetch,
    requestedReviewsList: requestedReviews.data?.data.reviews,
    requestedReviewsRefetch: requestedReviews.refetch,
    assignedReviewsList: assignedReviews.data?.data.reviews,
    assignedReviewsRefetch: assignedReviews.refetch,
    reviewsIsLoading: assignedReviews.isLoading || requestedReviews.isLoading,
    sendReview: reviewMutation.mutateAsync,
    reviewResponse: reviewMutation.data?.data,
    reviewIsPending: reviewMutation.isPending,
    answerReview: reviewAnswerMutation.mutateAsync,
    answerReviewIsPending: reviewAnswerMutation.isPending,
  };
};
